import { Icon16InfoOutline, Icon20PaymentCardOutline } from '@vkontakte/icons';
import { Link, Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import familyShareImage from 'img/freemium/benefitsModal/familyShare.png';
import familyShareDarkImage from 'img/freemium/benefitsModal/familyShareDark.png';
import moreImage from 'img/freemium/benefitsModal/more.png';
import moreDarkImage from 'img/freemium/benefitsModal/moreDark.png';
import space256Image from 'img/freemium/benefitsModal/space256.png';
import space256DarkImage from 'img/freemium/benefitsModal/space256Dark.png';
import unlimitedSpaceImage from 'img/freemium/benefitsModal/unlimitedSpace.png';
import unlimitedSpaceDarkImage from 'img/freemium/benefitsModal/unlimitedSpaceDark.png';
import uploadLimitImage from 'img/freemium/benefitsModal/uploadLimit.png';
import uploadLimitDarkImage from 'img/freemium/benefitsModal/uploadLimitDark.png';
import withoutAdsImage from 'img/freemium/benefitsModal/withoutAds.png';
import withoutAdsDarkImage from 'img/freemium/benefitsModal/withoutAdsDark.png';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isControlFreemium, isHardFreemium } from 'reactApp/appHelpers/featuresHelpers/features/freemium';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import {
    openFreemiumAboutModal,
    openFreemiumUnlimitedSpaceModal,
} from 'reactApp/sections/FamilyPage/FreemiumFeaturesModal/FreemiumFeaturesModal.helpers';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './FreemiumFeaturesModal.css';
import type { FreemiumFeaturesModalContent, FreemiumFeaturesModalProps, ListProps } from './FreemiumFeaturesModal.types';

const BenefitBlock: React.FC<ListProps> = ({ content, image, imageDark }) => {
    const isDarkTheme = useSelector(isCurrentDarkTheme);

    return (
        <div className={styles.benefitsBlock}>
            <img src={isDarkTheme ? imageDark : image} alt="Freemium" className={styles.image} />
            <div className={styles.content}>{content}</div>
        </div>
    );
};

const BankCardBlock = () => {
    const isDarkTheme = useSelector(isCurrentDarkTheme);
    return (
        <div className={classNames(styles.bankCardRoot, { [styles.bankCardDark]: isDarkTheme })}>
            <Icon20PaymentCardOutline fill="var(--vkui--color_icon_positive)" />
            <Text>Карту привязывать не нужно. Просто потестируйте подписку 7 дней</Text>
        </div>
    );
};

const ModalContent: React.FC<FreemiumFeaturesModalContent & { dwhName: string; showMoreDwhName?: string }> = ({
    tagImage,
    tagClassName,
    showBankCardBlock = false,
    tagImageDark,
    dwhName,
    showMoreDwhName,
}) => {
    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const showMoreName = showMoreDwhName || dwhName;

    const getListConfig = (): ListProps[] => [
        {
            image: unlimitedSpaceImage,
            imageDark: unlimitedSpaceDarkImage,
            content: (
                <div className={styles.positionRelative}>
                    <Text>Безлимитное место для&nbsp;фото с телефона</Text>
                    <Icon16InfoOutline
                        onClick={() => {
                            sendDwh({
                                eventCategory: 'modal',
                                action: 'show-more',
                                dwhData: {
                                    name: showMoreName,
                                },
                                dwh_enabled: true,
                            });
                            openFreemiumUnlimitedSpaceModal();
                        }}
                        className={styles.infoIcon}
                    />
                </div>
            ),
        },
        {
            image: familyShareImage,
            imageDark: familyShareDarkImage,
            content: 'Возможность пригласить  ещё 3 близких без доплаты',
        },
        {
            image: space256Image,
            imageDark: space256DarkImage,
            content: '+256 ГБ для того, что грузите с компьютера',
        },
        {
            image: withoutAdsImage,
            imageDark: withoutAdsDarkImage,
            content: 'Безрекламность — никаких баннеров в Облаке и Почте',
        },
        {
            image: uploadLimitImage,
            imageDark: uploadLimitDarkImage,
            content: 'Загрузка файлов до 100 ГБ: например, любимый фильм',
        },
        {
            image: moreImage,
            imageDark: moreDarkImage,
            content: (
                <>
                    <Link
                        onClick={() => {
                            sendDwh({
                                eventCategory: 'modal',
                                action: 'show-more',
                                dwhData: {
                                    name: showMoreName,
                                },
                                dwh_enabled: true,
                            });
                            openFreemiumAboutModal();
                        }}
                    >
                        Подробнее
                    </Link>{' '}
                    о пробном периоде подписки
                </>
            ),
        },
    ];

    return (
        <div className={styles.contentRoot}>
            <img src={isDarkTheme ? tagImageDark : tagImage} alt="Freemium" className={classNames(styles.tag, tagClassName)} />
            {showBankCardBlock && (
                <>
                    <BankCardBlock />
                    <Spacing size={24} />
                </>
            )}
            <Text className={styles.title}>Что ещё будет в подписке:</Text>
            <Spacing size={14} />
            <div className={styles.list}>
                {getListConfig().map(({ content, image, imageDark }: ListProps, index) => (
                    <BenefitBlock key={index} content={content} image={image} imageDark={imageDark} />
                ))}
            </div>
            <Spacing size={30} />
        </div>
    );
};

export const FreemiumFeaturesModal: React.FC<FreemiumFeaturesModalProps> = ({
    onClose,
    title,
    text,
    buttonText,
    showBankCardBlock,
    tagImage,
    tagClassName,
    tagImageDark,
    onButtonClick,
    dwhName: customDwhName,
    showMoreDwhName,
}) => {
    const defaultDwhName = isControlFreemium
        ? 'mnz_family_unlimited_trial'
        : isHardFreemium
        ? 'mnz_family_unlimited_hard_freemium_connected'
        : 'mnz_family_unlimited_soft_freemium';
    const dwhName = customDwhName || defaultDwhName;
    useEffect(() => {
        sendDwh({
            eventCategory: 'modal',
            action: 'show',
            dwhData: { name: dwhName },
            dwh_enabled: true,
        });
    }, []);

    const onCloseWrapper = () => {
        sendDwh({
            eventCategory: 'modal',
            action: 'close',
            dwhData: { name: dwhName },
            dwh_enabled: true,
        });
        onClose?.();
    };

    const onClickWrapper = () => {
        sendDwh({
            eventCategory: 'modal',
            action: 'click',
            dwhData: { name: dwhName },
            dwh_enabled: true,
        });
        onButtonClick?.();
    };
    return (
        <WhatsNewDialog
            title={title}
            text={text}
            imageUrl=""
            hideImage
            closable
            closeAfterClick
            content={
                <ModalContent
                    showBankCardBlock={showBankCardBlock}
                    tagImage={tagImage}
                    tagClassName={tagClassName}
                    tagImageDark={tagImageDark}
                    dwhName={dwhName}
                    showMoreDwhName={showMoreDwhName}
                />
            }
            textClassName={styles.text}
            primaryButtonText={buttonText}
            onClose={onCloseWrapper}
            bigCloseIcon={false}
            className={styles.root}
            onClick={onClickWrapper}
        />
    );
};
